import {
  ExperienceType,
  PayRequestPayload,
} from "checkout-query-client/api/types";
import { BankAccountType } from "checkout-query-client/configs/ecp-form-config";
import { MerchantSettings } from "checkout-query-client/hooks/use-merchant-settings";
import { usePubSub } from "checkout-query-client/hooks/use-pub-sub";
import {
  PubSubMessage,
  PubSubNamespace,
} from "checkout-query-client/publish-subcriber/types";
import { PaymentType } from "checkout-query-client/types/form-types";
import { buildBillingAddress } from "checkout-query-client/utils/build-payloads/build-billing-address";

import { IEncryptData, valueEncrypt } from "encrypt-value/encrypt";

import {
  EcpBankAccountType,
  EncryptedElectronicCheck,
  PayRequest,
  PayResponse_Status,
  PaymentMethod,
  PaymentMethod_Type,
} from "frontend-client";

import { getAcceptedMandates, getDeviceFingerprintSessionId } from "../helpers";

export const buildECPayload = async (
  payload: PayRequestPayload
): Promise<PayRequest> => {
  const { formFields, merchantSettings, experienceType, orderDigest } = payload;
  const request = new PayRequest();
  const encryptedElectronicCheck = new EncryptedElectronicCheck();
  const paymentMethod = new PaymentMethod();

  if (formFields && merchantSettings) {
    // Logic around Billing address
    const {
      billingAddressSameAsShipping,
      billingAddress,
      billingAddressFromIntentBilling,
    } = buildBillingAddress(payload, PaymentType.eCheck);
    encryptedElectronicCheck.billingAddress = billingAddress;
    encryptedElectronicCheck.billingAddressSameAsShipping =
      billingAddressSameAsShipping;
    request.billingAddressFromIntentBilling = billingAddressFromIntentBilling;

    const ecpPayload = await getECPPayload(
      merchantSettings,
      formFields,
      experienceType
    );
    if (ecpPayload.status === PayResponse_Status.SUCCESS && ecpPayload.ecp) {
      encryptedElectronicCheck.encryptedDdaAccountNumber =
        ecpPayload.ecp.encryptedAccountNumber;
      encryptedElectronicCheck.encryptedBankRoutingNumber =
        ecpPayload.ecp.encryptedRoutingNumber;
      encryptedElectronicCheck.encryptionKeyId = ecpPayload.ecp.encryptionKeyId;

      encryptedElectronicCheck.bankAccountType = getBankAccountType(
        ecpPayload.ecp.bankAccountType
      );
    }

    paymentMethod.electronicCheck = encryptedElectronicCheck;
    paymentMethod.type = PaymentMethod_Type.ELECTRONIC_CHECK;

    request.paymentMethod = paymentMethod;

    // text user consent "agreed" too
    request.consentToStoreForFuturePayments =
      formFields?.collectConsentToStoreForFuturePayments === "true"
        ? formFields?.collectConsentToStoreForFuturePaymentsText || ""
        : "";
    // setting payment method to default mop
    request.makeDefaultPaymentMethod = formFields?.setAsDefault === "true";
    // saving consumer profile based on the merchant intent.
    request.savePaymentMethodToProfile =
      formFields?.collectConsentToStoreForFuturePayments === "true";

    request.acceptedMandates = getAcceptedMandates(formFields);
    request.orderDigest = orderDigest ?? "";
    request.deviceFingerprintSessionId = getDeviceFingerprintSessionId();
  }

  return request;
};

const getBankAccountType = (
  bankAccountType: BankAccountType
): EcpBankAccountType => {
  if (bankAccountType === BankAccountType.savings) {
    return EcpBankAccountType.CUSTOMER_SAVINGS;
  } else if (bankAccountType === BankAccountType.commercialChecking) {
    return EcpBankAccountType.COMMERCIAL_CHECKING;
  }
  return EcpBankAccountType.CUSTOMER_CHECKING;
};

const getECPPayload = async (
  merchantSettings: MerchantSettings,
  formFields: Record<string, string>,
  experienceType?: ExperienceType
) => {
  let encryptedAccountNumber: IEncryptData;

  if (experienceType === "dropInUI") {
    encryptedAccountNumber = {
      encryptedData: formFields?.accountNumber ?? "",
      encryptionNonce: "",
      encryptionTimestamp: Date.now(),
    };
  } else {
    try {
      encryptedAccountNumber = await valueEncrypt({
        value: formFields?.accountNumber ?? "",
        key: merchantSettings?.encryptionKey ?? "",
      });
    } catch (e) {
      usePubSub().publish({
        level: "error",
        namespace: PubSubNamespace.payment,
        message: PubSubMessage.EcpAccountNumberEncryptionError,
      });
      return { status: PayResponse_Status.UNSPECIFIED };
    }
  }

  let encryptedRoutingNumber: IEncryptData | undefined;
  try {
    encryptedRoutingNumber = await valueEncrypt({
      value: formFields?.routingNumber ?? "",
      key: merchantSettings?.encryptionKey ?? "",
    });
  } catch (e) {
    usePubSub().publish({
      level: "error",
      namespace: PubSubNamespace.payment,
      message: PubSubMessage.EcpRoutingNumberEncryptionError,
    });
    return { status: PayResponse_Status.UNSPECIFIED };
  }

  if (!encryptedAccountNumber || !encryptedRoutingNumber) {
    usePubSub().publish({
      level: "error",
      namespace: PubSubNamespace.payment,
      message: PubSubMessage.PaymentError,
      payload: { error: "Failed to initialize encryption" },
    });
    return { status: PayResponse_Status.UNSPECIFIED };
  }

  return {
    status: PayResponse_Status.SUCCESS,
    ecp: {
      encryptedAccountNumber: encryptedAccountNumber.encryptedData,
      encryptedRoutingNumber: encryptedRoutingNumber.encryptedData,
      bankAccountType: formFields?.accountType as BankAccountType,
      encryptionKeyId: merchantSettings?.encryptionKeyId ?? "",
    },
  };
};
